import { BaseParser } from './BaseParser';
import { IInterfaceProperty, InterfacePropertyType } from '../../components/index';

const JSDOC_DEFAULT = '@default';
const JSDOC_DEFAULTVALUE = '@defaultvalue';
const JSDOC_DEPRECATED = '@deprecated';
/**
 * Supporting enum for the parser, used internally within the parser only.
 */
enum ParseState {
  default, comment, declaration
}

/**
 * Helper Parser that parses interfaces.
 */
export class InterfaceParserHelper extends BaseParser {
  private _state: ParseState = ParseState.default;

  /**
   * @constructor
   * Helper Parser that parses interfaces.
   */
  public constructor(str: string) {
    super(str);
  }

  public parse(): Array<IInterfaceProperty> {
    let bank: Array<string> = [];
    let comment = '';
    let identifierName = '';
    let type = '';
    let returnResult = [];
    let defaultValue = '';
    let isDeprecated = false;
    let deprecatedMessage = '';
    let noClosingSymbolAsteriskPrereq = false;

    this.eatUntil(/\{/);
    this.eat('{');

    do {
      switch (this._state) {
        case ParseState.default:
          this.eatSpacesAndNewlines();
          if (this.eat('/')) {
            if (this.peek() === '*') {
              this._state = ParseState.comment;
            } else {
              // ignore // comments
              this.eatUntil(/[\n]/);
            }
          } else if (this.eat('}')) {
            // closing
            break;
          }else {
            this._state = ParseState.declaration;
          }

          break;
        case ParseState.comment:
          {
            // the initial * are always the first * of a comment, and will be treated as decorative
            let asterisk = this.eatWhile('*');
            if ((noClosingSymbolAsteriskPrereq || asterisk.length > 0) && this.eat('/')) {
              // encountered closing comment tag
              comment = bank.join('').trim();
              bank = [];
              this._state = ParseState.default;
              break;
            }

            noClosingSymbolAsteriskPrereq = false;

            let tmp = this.eatUntil(/[\n\*@]/);
            bank.push(tmp);

            if (this.peek() === '*') {
              let tmp = this.eatWhile('*');
              if (this.peek() !== '/') {
                // encountered a line like '* This is a comment with asterisks in the middle **** like this.'
                bank.push(tmp);
              } else {
                // we have already encountered *, and the next symbol is /
                noClosingSymbolAsteriskPrereq = true;
              }
            } else if (this.peek() === '\n') {
              // go to next line
              this.eatSpacesAndNewlines();
            } else if (this.peek() === '@') {
              if (this.eatWord(JSDOC_DEFAULTVALUE) || this.eatWord(JSDOC_DEFAULT)) {
                // this parser assumes @default values won't have a bunch of asterisks in the middle of it.
                let tmp = this.eatUntil(/[\*\n]/);
                defaultValue = tmp;
                this.eatSpacesAndNewlines();
              } else if (this.eatWord(JSDOC_DEPRECATED)) {
                let tmp = this.eatUntil(/[\*\n]/);
                isDeprecated = true;
                deprecatedMessage = tmp;
              } else {
                bank.push(this.eat('@'));
              }
            }
          }
          break;
        case ParseState.declaration:
          {
            this.eatSpacesAndNewlines();
            let tmp = this.eatUntil(/[\:\;=]/);
            identifierName = tmp.trim();
            if (this.eat(':')) {
              tmp = this.eatUntil(/\;/);
              type = tmp;

            } else {
              // encountered semicolon or =
              type = 'unspecified';
            }

            this.eat(';'); // actually eat the semicolon

            let isOptional = identifierName[identifierName.length - 1] === '?';
            let propType = isDeprecated ? InterfacePropertyType.deprecated : (isOptional ? InterfacePropertyType.optional : InterfacePropertyType.required);

            this._state = ParseState.default;
            returnResult.push(<IInterfaceProperty>{
              description: comment,
              name: identifierName,
              type,
              defaultValue: defaultValue,
              interfacePropertyType: propType,
              deprecatedMessage
            });

            comment = identifierName = type = defaultValue = '';
          }
          break;
      }
    } while (this.hasNext());

    this.reset();
    return returnResult;
  }
}
